import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ViewerDashboard() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventMap, setEventMap] = useState({});
  const [loading, setLoading] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('viewer_token') : null;

  const filteredRegistrations = useMemo(() => registrations.filter(r => filterRegistration(r, query)), [registrations, query]);

  const totals = useMemo(() => {
    let count = filteredRegistrations.length;
    let paid = 0;
    let pending = 0;
    filteredRegistrations.forEach(r => {
      const status = (r.payment_status || r.status || r.paymentStatus || '').toString().toLowerCase();
      const amount = Number(r.amount ?? r.valor ?? r.value ?? 0) || 0;
      if (status === 'paid' || status === 'pago' || status === 'confirmed') paid += amount;
      else if (status === 'pending' || status === 'pendente') pending += amount;
    });
    return { count, paid, pending };
  }, [filteredRegistrations]);

  useEffect(() => {
    if (!token) {
      navigate('/viewer/login');
      return;
    }

    const fetchEventsInner = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/viewer/events`, { headers: { Authorization: `Bearer ${token}` } });
        setEvents(res.data);
        const map = {};
        res.data.forEach(ev => { map[ev.id] = ev; });
        setEventMap(map);
      } catch (err) {
        toast({ title: 'Erro', description: 'Não foi possível carregar eventos.', variant: 'destructive' });
        if (err.response?.status === 401) navigate('/viewer/login');
      } finally {
        setLoading(false);
      }
    };

    fetchEventsInner();
  }, [token, navigate, toast]);

  const fetchRegistrations = async (eventId) => {
    setSelectedEvent(eventId);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/viewer/registrations`, { headers: { Authorization: `Bearer ${token}` }, params: { event_id: eventId } });
      setRegistrations(res.data);
    } catch (err) {
      toast({ title: 'Erro', description: 'Não foi possível carregar inscrições.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('viewer_token');
    navigate('/viewer/login');
  };

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-primary">Acompanhamento de Inscrições</h1>
          <div>
            <Button onClick={handleLogout} variant="outline">Sair</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <h2 className="font-bold mb-3">Eventos autorizados</h2>
            {loading ? <p>Carregando...</p> : events.length === 0 ? <p>Nenhum evento disponível.</p> : (
              <ul className="space-y-2">
                {events.map(ev => (
                  <li key={ev.id} className="p-2 border rounded hover:bg-primary/5 cursor-pointer" onClick={() => fetchRegistrations(ev.id)}>
                    <div className="font-bold">{ev.name}</div>
                    <div className="text-sm text-foreground/70">{ev.date?.slice(0,10)} — {ev.location}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="lg:col-span-2 bg-card border border-border rounded-lg p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="font-bold mb-3">Inscrições {selectedEvent ? `do evento ${eventMap[selectedEvent]?.name || selectedEvent}` : ''}</h2>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Pesquisar participante, email, telefone ou ID"
                  className="w-full sm:w-64 px-3 py-2 border rounded bg-background text-foreground text-sm"
                />
                <Button onClick={() => setQuery('')}>Limpar</Button>
              </div>
            </div>
            {loading ? <p>Carregando...</p> : registrations.length === 0 ? <p>Nenhuma inscrição disponível para o evento selecionado.</p> : (
              <>
                <div className="flex flex-wrap gap-3 mb-3">
                  <div className="px-3 py-2 bg-gray-50 text-foreground rounded">Inscrições: <strong>{totals.count}</strong></div>
                  <div className="px-3 py-2 bg-green-50 text-green-800 rounded">Total Pago: <strong>{formatCurrency(totals.paid)}</strong></div>
                  <div className="px-3 py-2 bg-yellow-50 text-yellow-800 rounded">Total Pendente: <strong>{formatCurrency(totals.pending)}</strong></div>
                </div>
              <div className="overflow-x-auto">
                {/* Table for desktop/tablet */}
                <table className="w-full text-left border-collapse hidden lg:table">
                  <thead>
                    <tr className="text-sm text-foreground/70 border-b">
                      <th className="py-3 px-3">Participante</th>
                      <th className="py-3 px-3">Email</th>
                      <th className="py-3 px-3">Telefone</th>
                      <th className="py-3 px-3">Evento</th>
                      <th className="py-3 px-3">Categoria</th>
                      <th className="py-3 px-3">Valor</th>
                      <th className="py-3 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRegistrations.map(r => (
                      <tr key={r.id} className="border-b last:border-b-0 even:bg-background">
                        <td className="py-3 px-3 align-top">
                          <div className="font-bold">{r.nome} {r.sobrenome}</div>
                        </td>
                        <td className="py-3 px-3 align-top"><div className="text-sm text-foreground/70">{r.email}</div></td>
                        <td className="py-3 px-3 align-top"><div className="text-sm text-foreground/70">{r.telefone || r.whatsapp || '-'}</div></td>
                        <td className="py-3 px-3 align-top"><div className="text-sm text-foreground/70">{eventMap[r.event_id]?.name || r.event_id}</div></td>
                        <td className="py-3 px-3 align-top"><div className="text-sm text-foreground/70">{r.categoria || r.category || '-'}</div></td>
                        <td className="py-3 px-3 align-top"><div className="text-sm text-foreground/70">{formatCurrency(r.amount || r.valor || 0)}</div></td>
                        <td className="py-3 px-3 align-top">
                          {renderStatus(r.payment_status || r.status || r.paymentStatus)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Cards for mobile */}
                <div className="space-y-3 lg:hidden">
                  {filteredRegistrations.map(r => (
                    <div key={r.id} className="p-3 border rounded bg-card">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-bold">{r.nome} {r.sobrenome}</div>
                          <div className="text-sm text-foreground/70">{r.email}</div>
                          <div className="text-sm text-foreground/70">{r.telefone || r.whatsapp || '-'}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-foreground/70">{eventMap[r.event_id]?.name || r.event_id}</div>
                          <div className="text-sm text-foreground/70">{r.categoria || r.category || '-'}</div>
                          <div className="mt-2">{formatCurrency(r.amount || r.valor || 0)}</div>
                          <div className="mt-2">{renderStatus(r.payment_status || r.status || r.paymentStatus)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatCurrency(value) {
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value));
  } catch (e) {
    return `R$ ${value}`;
  }
}

function renderStatus(status) {
  const s = (status || '').toString().toLowerCase();
  const base = 'inline-block px-3 py-1 text-xs font-bold rounded-full';
  if (s === 'paid' || s === 'pago' || s === 'confirmed') return <span className={`${base} bg-green-100 text-green-800`}>Pago</span>;
  if (s === 'pending' || s === 'pendente') return <span className={`${base} bg-yellow-100 text-yellow-800`}>Pendente</span>;
  if (s === 'cancelled' || s === 'canceled' || s === 'cancelado') return <span className={`${base} bg-red-100 text-red-800`}>Cancelado</span>;
  return <span className={`${base} bg-gray-100 text-gray-800`}>{status || '-'}</span>;
}

function filterRegistration(r, q) {
  if (!q) return true;
  const s = q.toString().toLowerCase();
  const candidates = [r.id, r.nome, r.sobrenome, r.email, r.telefone, r.whatsapp, r.cpf, r.categoria, r.category, r.event_id];
  return candidates.some(c => c && c.toString().toLowerCase().includes(s));
}
